import { el } from '../dom.js';
import type { I18n } from '../i18n.js';

/**
 * §8 replay protection / GDPR. An explicit screen between the debrief and the
 * creation of a replay session.
 *
 * Two independent decision axes:
 *   1. Link the sessions? (§8 opt-in — without the checkbox priorSessionId is not stored)
 *   2. Play now anyway, despite the §9 G1 / §21.1 warning?
 *
 * Decline → back to landing; the cookie stays, so on the next POST /api/session
 * the new session is still marked isReplay=true from the cookie (R3), but
 * without a priorSessionId.
 *
 * Link → POST /api/session with replayConsent=true: priorSessionId is stored and
 * the role is picked as a contrast to the previous one.
 *
 * Continue (unlinked) → POST /api/session with replayConsent=false.
 */
export interface ReplayConsentProps {
  i18n: I18n;
  onContinue: (linkPriorSession: boolean) => void;
  onDecline: () => void;
}

export function ReplayConsentView({ i18n, onContinue, onDecline }: ReplayConsentProps): HTMLElement {
  let linkChecked = false;
  const checkbox = el('input', {
    type: 'checkbox',
    id: 'replay-link-consent',
    onChange: (ev: Event) => {
      linkChecked = (ev.target as HTMLInputElement).checked;
    },
  }) as HTMLInputElement;

  return el('section', { class: 'stack-lg', 'aria-label': i18n.t('replay.consent_heading') }, [
    el('header', {}, [
      el('h1', {}, i18n.t('replay.consent_heading')),
      el('p', { class: 'hero-lede' }, i18n.t('replay.consent_body')),
    ]),
    el('section', { class: 'debrief-section' }, [
      el('h2', {}, i18n.t('replay.consent_warning_heading')),
      el('p', {}, i18n.t('replay.consent_warning_body')),
    ]),
    el('section', { class: 'debrief-section' }, [
      el('label', { for: 'replay-link-consent', class: 'consent-row' }, [
        checkbox,
        el('span', {}, i18n.t('replay.consent_link_label')),
      ]),
      el('p', { class: 'hero-lede' }, i18n.t('replay.consent_link_note')),
    ]),
    el('div', { class: 'actions' }, [
      el('div', { class: 'actions-secondary' }, [
        el('button', { class: 'secondary', onClick: () => onDecline() }, i18n.t('replay.consent_decline')),
        el('button', { onClick: () => onContinue(linkChecked) }, i18n.t('replay.consent_continue')),
      ]),
    ]),
  ]);
}
